// Pure helpers — dept presets, search filter, and the guides-for-a-shift match.
import type { ConceptGuide } from "./api";

export const DEPT_OPTIONS = [
  { value: "all", label: "Chung", icon: "📚" },
  { value: "photo", label: "Photo", icon: "📸" },
  { value: "makeup", label: "Makeup", icon: "💄" },
  { value: "retouch", label: "Retouch", icon: "🎨" },
  { value: "sale", label: "Sale", icon: "💰" },
  { value: "other", label: "Khác", icon: "📦" },
] as const;

export type DeptValue = (typeof DEPT_OPTIONS)[number]["value"];

export function deptLabel(d: string | undefined): string {
  const found = DEPT_OPTIONS.find((o) => o.value === d);
  return found ? `${found.icon} ${found.label}` : "📚 Chung";
}

function norm(s: string): string {
  return (
    s
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      // "đ" is its own letter (U+0111), not a base letter plus a combining
      // mark, so NFD leaves it untouched — searching "set den" would miss
      // "set đèn" without this. Runs after NFD so "đ" with a tone mark
      // ("đầu") is already reduced to a bare "đ" by then.
      .replace(/đ/g, "d")
  );
}

function haystack(g: ConceptGuide): string {
  const parts: string[] = [g.title, g.description];
  if (g.tags) parts.push(g.tags.join(" "));
  if (g.steps) {
    for (const s of g.steps) {
      parts.push(s.title);
      if (s.body) parts.push(s.body);
    }
  }
  return norm(parts.join(" "));
}

export function filterGuides(
  list: ConceptGuide[],
  query: string,
  dept: string,
): ConceptGuide[] {
  const q = norm(query.trim());
  return list.filter((g) => {
    if (dept !== "all" && (g.dept ?? "all") !== dept) return false;
    if (!q) return true;
    return haystack(g).includes(q);
  });
}

// ── Guides for a shift (decision 0128) ──────────────────────────────────

/** The role a shift is worked in; dispatch slots are photo or makeup. */
export type ShiftRole = "photo" | "makeup";

/** What a shift is about, as far as guides care. Empty or absent = unknown. */
export type GuideTarget = {
  /** Concept the customer picked (orders.selected_concept). */
  selectedConcept?: string | undefined;
  /** Package code (orders.product), e.g. "cv_pro_makeup". */
  productCode?: string | undefined;
  /** Product line code (orders.category), e.g. "idphoto". */
  category?: string | undefined;
};

/** Match tiers, strongest first: the shift's concept, its package, its line. */
export type GuideTier = "concept" | "product" | "line";

export type GuideGroup = {
  tier: GuideTier;
  /** The concept id, package code or line code the group matched on. */
  key: string;
  guides: ConceptGuide[];
};

/** Display names for groups and labels; `concept` returns "" when unknown. */
export type GuideNames = {
  concept: (id: string) => string;
  product: (code: string) => string;
  line: (code: string) => string;
};

// A guide without a dept is a "Chung" guide. Guides of the other departments
// (retouch, sale, other) never show on a shift.
function seenBy(g: ConceptGuide, role: ShiftRole): boolean {
  const dept = g.dept || "all";
  return dept === "all" || dept === role;
}

/**
 * The guides to show on a shift, grouped concept → package → line (CEO answer
 * 2a: concept guides first, then the product-type ones, no duplicates). A
 * guide matching several tiers shows once, under the strongest; guides keep
 * the library order inside a group. Empty groups are left out, so a shift
 * with no matching guide gets [].
 */
export function matchGuides(
  target: GuideTarget,
  guides: readonly ConceptGuide[],
  role: ShiftRole,
): GuideGroup[] {
  const tiers: [GuideTier, string | undefined, (g: ConceptGuide, key: string) => boolean][] = [
    ["concept", target.selectedConcept, (g, key) => g.conceptId === key],
    ["product", target.productCode, (g, key) => g.productCodes?.includes(key) ?? false],
    ["line", target.category, (g, key) => g.lineCodes?.includes(key) ?? false],
  ];
  const visible = guides.filter((g) => seenBy(g, role));
  const shown = new Set<ConceptGuide>();
  const out: GuideGroup[] = [];
  for (const [tier, key, matches] of tiers) {
    if (!key) continue;
    const hit = visible.filter((g) => !shown.has(g) && matches(g, key));
    if (hit.length === 0) continue;
    for (const g of hit) shown.add(g);
    out.push({ tier, key, guides: hit });
  }
  return out;
}

// Concept guides are titled after their concept (the editor pre-fills it), so
// the first title is a fair stand-in while the concept list is unavailable.
function conceptName(group: GuideGroup, names: Pick<GuideNames, "concept">): string {
  return names.concept(group.key) || group.guides[0]?.title || group.key;
}

/**
 * The line under "Hướng dẫn cho ca này": "2 bài · theo concept Hiện Đại",
 * "5 bài · theo loại sản phẩm", or both joined by "và". "" when no guide.
 */
export function guideSummary(groups: readonly GuideGroup[], names: Pick<GuideNames, "concept">): string {
  const total = groups.reduce((n, g) => n + g.guides.length, 0);
  if (total === 0) return "";
  const concept = groups.find((g) => g.tier === "concept");
  const byType = groups.some((g) => g.tier !== "concept");
  const basis = [concept ? `concept ${conceptName(concept, names)}` : "", byType ? "loại sản phẩm" : ""]
    .filter(Boolean)
    .join(" và ");
  return `${total} bài · theo ${basis}`;
}

/** Heading of a group in the guide dialog. */
export function groupTitle(group: GuideGroup, names: GuideNames): string {
  switch (group.tier) {
    case "concept":
      return `Theo concept ${conceptName(group, names)}`;
    case "product":
      return `Theo gói ${names.product(group.key)}`;
    case "line":
      return `Chung cho cả dòng ${names.line(group.key)}`;
  }
}

// "CV Basic (có make)" → base "CV Basic", variant "có make". NFC first: names
// typed on a Mac arrive decomposed and would not match.
const MAKE_VARIANT = /^(.*?)\s*\((có make|không make)\)$/iu;

function packagesLabel(codes: readonly string[], product: (code: string) => string): string {
  const full = codes.map((code) => product(code).normalize("NFC"));
  if (full.length <= 1) return full.join("");
  const bases: string[] = [];
  const variants = new Set<string>();
  for (const name of full) {
    const m = MAKE_VARIANT.exec(name);
    const base = m?.[1] ?? name;
    const variant = m?.[2]?.toLowerCase();
    if (variant) variants.add(variant);
    if (!bases.includes(base)) bases.push(base);
  }
  const kinds = ["có make", "không make"].filter((k) => variants.has(k)).join(" và ");
  const count = [`${codes.length} gói`, kinds].filter(Boolean).join(", ");
  return `${bases.join(" · ")} (${count})`;
}

/**
 * The "Áp cho: …" line of a guide in the library: whole lines first ("cả dòng
 * Ảnh thẻ"), then packages. One package keeps its full name; several are
 * listed by base name with the "(có make)" / "(không make)" twins merged and
 * the count said once, e.g. "CV Basic · CV Tốt nghiệp (4 gói, có make và
 * không make)". "" when the guide is tied to no product type.
 */
export function appliesToLabel(
  guide: Pick<ConceptGuide, "lineCodes" | "productCodes">,
  names: Pick<GuideNames, "product" | "line">,
): string {
  const parts = (guide.lineCodes ?? []).map((code) => `cả dòng ${names.line(code)}`);
  const packages = packagesLabel(guide.productCodes ?? [], (code) => names.product(code));
  if (packages) parts.push(packages);
  return parts.join(" · ");
}

const WEEKDAYS = ["Chủ Nhật", "Thứ Hai", "Thứ Ba", "Thứ Tư", "Thứ Năm", "Thứ Sáu", "Thứ Bảy"];
// Same words as the dispatch slot card, so the dialog names the seat the same way.
const ROLE_LABEL: Record<ShiftRole, string> = { photo: "Thợ chụp", makeup: "Makeup" };

/**
 * The guide dialog's sub-heading naming the shift, e.g. "Thứ Hai 14/09 · 10:00
 * · CV Chuyên nghiệp (có make) · vai Thợ chụp". The time is cut to hh:mm and
 * left out when unset; a shift without a date says so instead.
 */
export function shiftSubtitle(shift: {
  shootDate: string;
  startTime?: string | undefined;
  /** The package's display name, not its code. */
  product: string;
  role: ShiftRole;
}): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(shift.shootDate);
  // Built from the calendar parts in local time, so the weekday cannot slip a
  // day the way parsing "2026-09-14" as UTC midnight would west of Greenwich.
  const day = m
    ? `${WEEKDAYS[new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getDay()]} ${m[3]}/${m[2]}`
    : "Chưa chốt ngày";
  const time = (shift.startTime ?? "").slice(0, 5);
  return [day, time, shift.product, `vai ${ROLE_LABEL[shift.role]}`].filter(Boolean).join(" · ");
}
